import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { ViewMode } from '@/components/common/ViewToggle';
import {
	CompanyPosition,
	PositionCandidate,
	SeniorityBreakdown,
	groupSimilarPositions,
	sortCandidatesByRecency,
	extractSeniority,
} from '@/utils/rolesGrouping';

const emptyBreakdown = (): SeniorityBreakdown => ({
	lead: 0,
	senior: 0,
	mid: 0,
	junior: 0,
	unknown: 0,
});

// Re-export types for backwards compatibility with existing imports
export type { CompanyPosition, PositionCandidate } from '@/utils/rolesGrouping';

export interface UseCompanyPositionsReturn {
	positions: CompanyPosition[];
	loading: boolean;
	error: string | null;
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	viewMode: ViewMode;
	setViewMode: (mode: ViewMode) => void;
	refreshPositions: () => Promise<void>;
}

/**
 * COMPANY POSITIONS HOOK WITH INTELLIGENT GROUPING
 *
 * Fetches and processes company position data with smart grouping logic.
 *
 * DATA SOURCES:
 * - experiences table: Direct candidate experiences
 * - candidates.ai_analysis: AI-extracted experience data
 */
export const useCompanyPositions = (companyId: string, companyName: string): UseCompanyPositionsReturn => {
	const [positions, setPositions] = useState<CompanyPosition[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [viewMode, setViewMode] = useState<ViewMode>('card');
	const { currentOrganization } = useAuth();

	const fetchPositions = async () => {
		if (!companyName) {
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const [experiencesResult, candidatesResult] = await Promise.all([
				supabase
					.from('experiences')
					.select(`
						id,
						title,
						dates,
						company,
						candidate_id,
						candidates (
							id,
							name
						)
					`)
					.or(`company.ilike.%${companyName}%,company.ilike.%${companyName.toUpperCase()}%,company.ilike.%${companyName.toLowerCase()}%,company.ilike.%GAMING1%,company.ilike.%gaming1%`)
					.order('title', { ascending: true }),

				supabase
					.from('candidates')
					.select(`
						id,
						name,
						ai_analysis
					`)
					.not('ai_analysis', 'is', null)
			]);

			if (experiencesResult.error)
				throw experiencesResult.error;
			if (candidatesResult.error)
				throw candidatesResult.error;

			const experiencesData = experiencesResult.data || [];

			const aiExperiencesData: any[] = [];
			if (candidatesResult.data) {
				candidatesResult.data.forEach(candidate => {
					if (candidate.ai_analysis?.roles_jobs_companies_dates?.["Extracted Value"]) {
						const aiExperiences = candidate.ai_analysis.roles_jobs_companies_dates["Extracted Value"];
						aiExperiences.forEach((exp: any) => {
							const companyMatch = exp.company && (() => {
								const expCompany = exp.company.toLowerCase().trim();
								const targetCompany = companyName.toLowerCase().trim();

								if (expCompany === targetCompany) return true;

								const expWords = expCompany.split(/\s+/).filter((word: string) => word.length > 2);
								const targetWords = targetCompany.split(/\s+/).filter((word: string) => word.length > 2);

								if (targetWords.length >= 3) {
									const matchingWords = targetWords.filter((word: string) =>
										expWords.some((expWord: string) => expWord.includes(word) || word.includes(expWord))
									);
									if (matchingWords.length >= 2) return true;
								}

								if (targetWords.length === 2) {
									const matchingWords = targetWords.filter((word: string) =>
										expWords.some((expWord: string) => expWord.includes(word) || word.includes(expWord))
									);
									if (matchingWords.length === 2) return true;
								}

								if (targetWords.length === 1) {
									return expWords.some((expWord: string) =>
										expWord === targetWords[0] ||
										(expWord.length > 4 && targetWords[0].length > 4 &&
											(expWord.includes(targetWords[0]) || targetWords[0].includes(expWord)))
									);
								}

								return false;
							})();

							if (companyMatch) {
								const title = exp.title || exp.role || exp.job || exp.position || 'Unknown Position';

								aiExperiencesData.push({
									id: `ai_${candidate.id}_${exp.company}_${title}`,
									title: title,
									dates: exp.dates || '',
									company: exp.company,
									candidate_id: candidate.id,
									candidates: {
										id: candidate.id,
										name: candidate.name
									}
								});
							}
						});
					}
				});
			}

			const allData = [...experiencesData, ...aiExperiencesData];

			if (allData.length === 0) {
				setPositions([]);
				return;
			}

			const positionMap = new Map<string, PositionCandidate[]>();

			allData.forEach((experience) => {
				if (!experience.candidates) return;

				const title = experience.title || 'Unknown Position';
				const { level: seniority } = extractSeniority(title);

				const candidate: PositionCandidate = {
					id: experience.candidates.id,
					firstName: experience.candidates.name || '',
					lastName: '',
					dates: experience.dates || '',
					experienceId: experience.id,
					originalTitle: title,
					seniority,
				};

				if (!positionMap.has(title))
					positionMap.set(title, []);

				positionMap.get(title)!.push(candidate);
			});

			const rawPositions: CompanyPosition[] = Array.from(positionMap.entries()).map(([title, candidates]) => ({
				title,
				candidateCount: candidates.length,
				candidates: sortCandidatesByRecency(candidates),
				companies: [],
				seniorityBreakdown: emptyBreakdown(),
			}));

			const positionsArray: CompanyPosition[] = groupSimilarPositions(rawPositions)
				.sort((a, b) => b.candidateCount - a.candidateCount);

			setPositions(positionsArray);
		} catch (err) {
			console.error('Error fetching company positions:', err);
			setError(err instanceof Error ? err.message : 'Failed to fetch positions');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchPositions();
	}, [companyName]);

	const needle = searchTerm.toLowerCase();
	const filteredPositions = positions.filter(position =>
		position.title.toLowerCase().includes(needle) ||
		position.candidates.some(candidate =>
			candidate.firstName.toLowerCase().includes(needle) ||
			candidate.originalTitle.toLowerCase().includes(needle)
		)
	);

	return {
		positions: filteredPositions,
		loading,
		error,
		searchTerm,
		setSearchTerm,
		viewMode,
		setViewMode,
		refreshPositions: fetchPositions
	};
};
